import mermaid from "mermaid";
import type { MermaidConfig } from "mermaid";
import type { Diagram } from "mermaid/dist/Diagram.js";
import type { FlowDB } from "mermaid/dist/diagrams/flowchart/flowDb.js";
import type { ErDB } from "mermaid/dist/diagrams/er/erDb.js";
import type { StateDB } from "mermaid/dist/diagrams/state/stateDb.js";

import { GraphImage } from "./interfaces.js";
import { MERMAID_CONFIG } from "./constants.js";
import { encodeEntities } from "./utils.js";
import { Flowchart, parseMermaidFlowChartDiagram } from "./parser/flowchart.js";
import { Sequence, parseMermaidSequenceDiagram } from "./parser/sequence.js";
import { Class, parseMermaidClassDiagram } from "./parser/class.js";
import { ERD, parseMermaidERDiagram } from "./parser/er.js";
import { State, parseMermaidStateDiagram } from "./parser/state.js";
import { Mindmap, parseMermaidMindmapDiagram } from "./parser/mindmapParser.js";
let mermaidTaskQueue: Promise<void> = Promise.resolve();
const runMermaidTaskSequentially = <T>(task: () => Promise<T>) => {
  const run = mermaidTaskQueue.then(task, task);
  mermaidTaskQueue = run.then(
    () => undefined,
    () => undefined
  );
  return run;
};

let lastConfigHash: string | null = null;
let renderCounter = 0;

const hashConfig = (config: MermaidConfig): string => {
  return JSON.stringify(config);
};

export const parseMermaid = async (
  definition: string,
  config: MermaidConfig = MERMAID_CONFIG
): Promise<Flowchart | GraphImage | Sequence | Class | ERD | State | Mindmap> => {
  return runMermaidTaskSequentially(async () => {
    const currentConfigHash = hashConfig(config);
    if (currentConfigHash !== lastConfigHash) {
      mermaid.initialize(config);
      lastConfigHash = currentConfigHash;
    }

    const id = `mermaid-to-excalidraw-${renderCounter++}`;
    
    // In Mermaid v10+, we need to get the diagram instance separately
    const diagram = await (mermaid as any).mermaidAPI.getDiagramFromText(definition);
    const { svg } = await mermaid.mermaidAPI.render(
      id,
      encodeEntities(definition)
    );

    const svgContainer = document.createElement("div");
    svgContainer.style.position = "absolute";
    svgContainer.style.top = "-9999px";
    svgContainer.style.left = "-9999px";
    svgContainer.style.visibility = "hidden";
    svgContainer.classList.add("mermaid-to-excalidraw-svg-container");

    try {
      document.body.appendChild(svgContainer);

      // Append SVG to DOM temporarily to allow querying element dimensions/positions
      svgContainer.innerHTML = svg;

      let data: Flowchart | GraphImage | Sequence | Class | ERD | State | Mindmap;

      try {
        switch (diagram.type) {
          case "flowchart-v2":
          case "flowchart": {
            data = parseMermaidFlowChartDiagram(
              diagram.db as FlowDB,
              svgContainer
            );
            break;
          }
          case "sequence": {
            data = parseMermaidSequenceDiagram(diagram, svgContainer);
            break;
          }
          case "classDiagram":
          case "class": {
            data = parseMermaidClassDiagram(diagram.db, svgContainer);
            break;
          }
          case "er": {
            data = parseMermaidERDiagram(diagram.db as ErDB, svgContainer);
            break;
          }
          case "stateDiagram": {
            data = parseMermaidStateDiagram(
              diagram.db as StateDB,
              svgContainer
            );
            break;
          }
          case "mindmap": {
            data = parseMermaidMindmapDiagram(diagram.db, svgContainer);
            break;
          }
          default: {
            data = convertSvgToGraphImage(svgContainer);
          }
        }
      } catch (e) {
        console.error("Failed to parse mermaid diagram", e);
        data = convertSvgToGraphImage(svgContainer);
      }

      return data;
    } finally {
      svgContainer.remove();
    }
  });
};

const convertSvgToGraphImage = (svgContainer: HTMLElement): GraphImage => {
  const svgEl = svgContainer.querySelector("svg");
  if (!svgEl) {
    throw new Error("Failed to find SVG element");
  }

  const { width, height } = svgEl.viewBox.baseVal;
  return {
    type: "graphImage",
    mimeType: "image/svg+xml",
    dataURL: `data:image/svg+xml;base64,${btoa(svgEl.outerHTML)}`,
    width,
    height,
  };
};
// Force HMR reload
